import { LoginTranslation } from '../../utils/helper.type';
import { Trans } from 'react-i18next';
import { renderPasswordInput, renderSubmitButton } from '../login/loginHelper';
import TabletBackButton from '../../component/tabletBackButton/tabletBackButton';
import TabletLayoutImage from '../../component/tabletLayoutImage/tabletLayoutImage';
import DesktopLayoutImage from '../../component/desktopLayoutmage/desktopLayoutImage';
import RenderInPlayLogo from '../../component/inPlayLogo/inPlayLogo';

/**
 * Renders the tablet view for the Reset Password page.
 *
 * @param handleSubmit - Function to handle the password reset form submission
 * @param password - Current value of the new password
 * @param setPassword - Function to update the new password state
 * @param confirmPassword - Current value of the confirm password
 * @param setConfirmPassword - Function to update the confirm password state
 * @param loginTranslation - Translation object for login fields
 * @param showPassword - Boolean to toggle visibility of new password
 * @param setShowPassword - Function to toggle new password visibility
 * @param showConfirmPassword - Boolean to toggle visibility of confirm password
 * @param setShowConfirmPassword - Function to toggle confirm password visibility
 * @param error - Error message if passwords don't match or validation fails
 * @param handleBackToLogin - Function to navigate back to the login screen
 * @returns JSX.Element
 */
export const renderTabletView = (
  handleSubmit: (e: React.FormEvent) => void,
  password: string,
  setPassword: React.Dispatch<React.SetStateAction<string>>,
  confirmPassword: string,
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation: LoginTranslation,
  showPassword: boolean,
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>,
  showConfirmPassword: boolean,
  setShowConfirmPassword: React.Dispatch<React.SetStateAction<boolean>>,
  error: string | null,
  handleBackToLogin: () => void,
  passwordMainTitle: string,
  passwordButton: string,
  isLoading: boolean,
  isDisable?: boolean,
) => {
  return (
    <div className="relative min-h-screen w-full" data-testid="tablet-view">
      <TabletLayoutImage data-testid="tablet-layout-image" />

      <div className="absolute inset-0 bg-black/30" data-testid="overlay" />

      <TabletBackButton data-testid="back-button" onClick={handleBackToLogin} />
      <div className="tablet-login-container" data-testid="login-container">
        <div className="flex flex-col">
          <div className="text-white mt-auto mb-20">
            <RenderInPlayLogo className="mb-10 h-10" data-testid="logo" />
            <h1
              className="text-4xl sm:text-5xl login-font-style mb-2"
              data-testid="password-title"
            >
              <Trans i18nKey={passwordMainTitle} />
            </h1>
          </div>
        </div>

        <div className="flex flex-col justify-center items-center h-full">
          <div className="tablet-login-form" data-testid="form-container">
            <form onSubmit={handleSubmit} data-testid="tablet-form">
              {renderPasswordInput(
                <Trans i18nKey="login.newPassword" />,
                password,
                setPassword,
                loginTranslation.password,
                showPassword,
                setShowPassword,
                !!error,
              )}
              {renderPasswordInput(
                <Trans i18nKey="login.confirmPassword" />,
                confirmPassword,
                setConfirmPassword,
                loginTranslation.password,
                showConfirmPassword,
                setShowConfirmPassword,
                !!error,
              )}
              {error && (
                <p
                  className="text-base text-[#D80000] mt-2"
                  data-testid="error-message"
                >
                  {error}
                </p>
              )}
              {renderSubmitButton(
                isDisable ?? false,
                isLoading,
                <Trans i18nKey={passwordButton} />,
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the desktop view for the Reset Password page.
 *
 * @param handleSubmit - Function to handle the password reset form submission
 * @param password - Current value of the new password
 * @param setPassword - Function to update the new password state
 * @param confirmPassword - Current value of the confirm password
 * @param setConfirmPassword - Function to update the confirm password state
 * @param loginTranslation - Translation object for login fields
 * @param showPassword - Boolean to toggle visibility of new password
 * @param setShowPassword - Function to toggle new password visibility
 * @param showConfirmPassword - Boolean to toggle visibility of confirm password
 * @param setShowConfirmPassword - Function to toggle confirm password visibility
 * @param error - Error message if passwords don't match or validation fails
 * @returns JSX.Element
 */
export const renderDesktopView = (
  handleSubmit: (e: React.FormEvent) => void,
  password: string,
  setPassword: React.Dispatch<React.SetStateAction<string>>,
  confirmPassword: string,
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation: LoginTranslation,
  showPassword: boolean,
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>,
  showConfirmPassword: boolean,
  setShowConfirmPassword: React.Dispatch<React.SetStateAction<boolean>>,
  error: string | null,
  passwordMainTitle: string,
  passwordButton: string,
  isLoading: boolean,
  isDisable?: boolean,
) => {
  return (
    <div className="lg:flex h-screen w-full" data-testid="desktop-view">
      <div className="flex flex-col flex-1 px-10 py-6">
        <div
          className="flex flex-col max-w-[520px] w-full mx-auto"
          data-testid="desktop-container"
        >
          <div className="mt-[200px]" data-testid="desktop-inner">
            <RenderInPlayLogo data-testid="desktop-logo" />
            <div
              className="login-header-container"
              data-testid="desktop-header-container"
            >
              <h1
                className="text-6xl text-[#1A1A1A] mb-16"
                data-testid="desktop-password-title"
              >
                <Trans i18nKey={passwordMainTitle} />
                <br />
                <Trans i18nKey="login.password" />
              </h1>
            </div>

            <form onSubmit={handleSubmit} data-testid="desktop-form">
              {renderPasswordInput(
                <Trans i18nKey="login.newPassword" />,
                password,
                setPassword,
                loginTranslation.password,
                showPassword,
                setShowPassword,
                !!error,
              )}
              {renderPasswordInput(
                <Trans i18nKey="login.confirmPassword" />,
                confirmPassword,
                setConfirmPassword,
                loginTranslation.password,
                showConfirmPassword,
                setShowConfirmPassword,
                !!error,
              )}

              {error && (
                <p
                  className="text-base text-[#D80000] mt-2"
                  data-testid="desktop-error"
                >
                  {error}
                </p>
              )}

              <div className="mt-20" data-testid="desktop-submit-container">
                {renderSubmitButton(
                  isDisable ?? false,
                  isLoading,
                  <Trans i18nKey={passwordButton} />,
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <div data-testid="desktop-image">
        <DesktopLayoutImage />
      </div>
    </div>
  );
};
